import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { formatApiError } from "../lib/api";
import { BookOpen } from "@phosphor-icons/react";
import InfoHint from "../components/InfoHint";
import PasswordInput from "../components/PasswordInput";

const HERO_IMG = "https://static.prod-images.emergentagent.com/jobs/33c49a49-4b5d-43fb-a2a2-4b27681abf6a/images/100e04a3fadaf704cc02264fad3969b261c773aa0b60913c93c05067c977e5d3.png";

export default function Register() {
  const { register, loginWithGoogle } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await register(email, password, name);
      navigate("/bookmarks");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-80px)] grid grid-cols-1 lg:grid-cols-2 animate-fade-up">
      <div className="relative hidden lg:block">
        <img src={HERO_IMG} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-br from-terracotta/70 to-forest/80" />
        <div className="absolute bottom-12 left-12 right-12 text-cream">
          <BookOpen size={40} weight="duotone" />
          <p className="font-heading text-4xl mt-6 italic leading-tight">"For where two or three gather in my name, there am I."</p>
          <p className="text-cream/70 text-sm mt-3">Matthew 18:20</p>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 md:p-12">
        <form onSubmit={onSubmit} className="w-full max-w-sm" data-testid="register-form">
          <div className="flex items-center gap-2.5 flex-wrap mt-2"><h1 className="font-heading text-4xl text-forest font-medium">Begin your journey</h1><InfoHint testid="register-info">Free forever. Save verses, build your reading rhythm.</InfoHint></div>

          <label className="block mt-7 text-[10px] tracking-[0.2em] uppercase text-muted_ink font-semibold">Name (optional)</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            data-testid="register-name-input"
            className="mt-1 w-full h-12 px-4 rounded-xl bg-white border border-cream-300 outline-none focus:border-forest text-sm"
          />

          <label className="block mt-4 text-[10px] tracking-[0.2em] uppercase text-muted_ink font-semibold">Email</label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            data-testid="register-email-input"
            className="mt-1 w-full h-12 px-4 rounded-xl bg-white border border-cream-300 outline-none focus:border-forest text-sm"
          />

          <label className="block mt-4 text-[10px] tracking-[0.2em] uppercase text-muted_ink font-semibold">Password (min 6)</label>
          <PasswordInput
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            minLength={6}
            testId="register-password-input"
            className="mt-1 w-full h-12 px-4 rounded-xl bg-white border border-cream-300 outline-none focus:border-forest text-sm"
          />

          {error && <p className="text-red-600 text-xs mt-3" data-testid="register-error">{error}</p>}

          <button
            disabled={busy}
            data-testid="register-submit-button"
            className="mt-6 w-full h-12 rounded-full bg-terracotta hover:bg-terracotta-600 text-cream text-sm font-medium transition-all hover:scale-[1.02] disabled:opacity-50 shadow-md"
          >
            {busy ? "Creating…" : "Create Account"}
          </button>

          <div className="my-5 flex items-center gap-3">
            <div className="flex-1 h-px bg-cream-300" />
            <span className="text-[10px] tracking-[0.2em] uppercase text-muted_ink">or</span>
            <div className="flex-1 h-px bg-cream-300" />
          </div>

          <button
            type="button"
            onClick={loginWithGoogle}
            data-testid="google-signup-button"
            className="w-full h-12 rounded-full bg-white border border-cream-300 hover:border-forest text-ink text-sm font-medium transition-all hover:scale-[1.02] flex items-center justify-center gap-2.5"
          >
            <svg width="18" height="18" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09 0-.72.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
            Continue with Google
          </button>

          <p className="text-sm text-muted_ink mt-6">
            Already have an account?{" "}
            <Link to="/login" data-testid="register-to-login" className="text-terracotta hover:underline">
              Sign in
            </Link>
          </p>
        </form>
      </div>
    </div>
  );
}
